import * as React from 'react';
import { TextField } from '@mui/material';
import ConfirmationButtons from '../button/ConfirmationButtons';
import TestKitsDropDownMultiselect from '../drop-down/TestKitsDropdownMultiSelect';
import cloneDeep from 'lodash/cloneDeep';
import DefaultNameTag from '../site/DefaultNameTag';
import { isDefaultLocationName } from 'utils/constants/starterSite';

interface LocationsTableProps {
	headers: any[];
	locations: any[];
	isOwner?: boolean;
	testKits: any[];
	handleEditLocation: (editedLocation: any) => void;
	handleDeleteLocation: (deleteLocation: any) => void;
	setLocations: React.Dispatch<React.SetStateAction<any[]>>;
}

const LocationsTable: React.FC<LocationsTableProps> = ({
	headers,
	locations,
	isOwner,
	testKits,
	handleEditLocation,
	handleDeleteLocation,
	setLocations,
}) => {
	const [showConfirmationLoading, setShowConfirmationLoading] =
		React.useState(false);
	const [showDeleteAction, setShowDeleteAction] = React.useState(false);
	const [showEditAction, setShowEditAction] = React.useState(false);
	const [selectedLocation, setSelectedLocation] = React.useState(0);
	const [editedLocation, setEditedLocation] = React.useState({
		name: '',
	});
	const [locationNameError, setLocationNameError] = React.useState('');

	const handleDeleteConfirm = async (location: any) => {
		setShowConfirmationLoading(true);
		handleDeleteLocation(location);
		setShowConfirmationLoading(false);
	};

  const handleSelectedKits = async (selectedKits: any, index: number) => {
    setShowConfirmationLoading(true);
    const newLocations = cloneDeep(locations);
    newLocations[index].testKitDescriptors = selectedKits;
    setEditedLocation(newLocations[index]);
    handleEditLocation(newLocations[index]);
    setShowConfirmationLoading(false);
};

	const handleConfirmEditClick = async (location: any, index: number) => {
		setShowConfirmationLoading(true);
		if (editedLocation.name.trim() === '')
			return setLocationNameError('Location name is required');
		handleEditLocation(editedLocation);
		setShowConfirmationLoading(false);
		setShowEditAction(false);
		setShowDeleteAction(false);
	};

	const handleConfirmDeleteClick = async (location: any, index: number) => {
		setShowConfirmationLoading(true);
		await handleDeleteConfirm(location);
		setShowConfirmationLoading(false);
		setShowEditAction(false);
		setShowDeleteAction(false);
	};

	const handleEditName = (value: string, index: number) => {
		setEditedLocation({ ...locations[index], name: value });
		setShowEditAction(true);
		setShowDeleteAction(false);
		setSelectedLocation(index);
	};

	const handleCancelClick = () => {
		setShowDeleteAction(false);
		setShowEditAction(false);
		setEditedLocation({ name: '' });
	};

	React.useEffect(() => {}, [
		showConfirmationLoading,
		locations,
		showEditAction,
	]);

	return (
		<div>
			<div className="mt-[8px]">
				{locations.map((location, index) => (
					<div
						className="border-b-[1px] border-[#E6E6E6] pb-[8px] mt-[8px]"
						key={`${location.name}-${index}`}
					>
						<div
							className="
                grid grid-cols-12
                font-inter font-[600] text-[14px] tracking-[0.15px] leading-[17px] text-[#666666]
                pb-[5px]
              "
						>
							{headers.map((header) => (
								<div
									className={`${header.colSpan}`}
									key={header.name}
								>
									{header.name}
								</div>
							))}
						</div>
						<div
							className="
              grid grid-cols-12
              font-inter font-[600] text-[14px] tracking-[0.15px] leading-[17px] text-[#666666]
              pb-[8px]
            "
						>
							<div className="col-span-4">
								<TextField
									value={
										showEditAction &&
										selectedLocation === index
											? editedLocation.name
											: location.name
									}
									size="small"
									style={{
										width: 320,
									}}
									onChange={(event) =>
										handleEditName(
											event.target.value,
											index
										)
									}
									error={locationNameError !== ''}
									helperText={locationNameError}
									InputProps={{
										endAdornment:
											isDefaultLocationName(location.name) &&
											!(showEditAction && selectedLocation === index) ? (
												<DefaultNameTag />
											) : undefined,
									}}
								/>
							</div>
							<div className="col-span-4">
								<TestKitsDropDownMultiselect
									data={testKits || []}
									selectedData={location.testKitDescriptors}
									handleSelectedData={(data) =>
										handleSelectedKits(data, index)
									}
									error={''}
									size="small"
								/>
							</div>
							{isOwner && (
								<div className="col-span-4 text-end self-center">
									{(showDeleteAction || showEditAction) &&
									selectedLocation === index ? (
										<div
											className={`flex flex-row gap-[15px] justify-end ${
												showConfirmationLoading &&
												'cursor-progress'
											}`}
										>
											<div className="text-[14px] font-[600] text-[#556670] self-center">
												{showDeleteAction
													? 'DELETE'
													: 'EDIT'}
											</div>
											<ConfirmationButtons
												handleConfirm={() => {
													if (showDeleteAction) {
														handleConfirmDeleteClick(
															location,
															index
														);
													} else {
														handleConfirmEditClick(
															location,
															index
														);
													}
												}}
												handleCancel={handleCancelClick}
												disabled={
													showConfirmationLoading
												}
											/>
										</div>
									) : (
										<button
											type="button"
											onClick={() => {
												setShowDeleteAction(true);
												setSelectedLocation(index);
											}}
										>
											<img
												src="assets/icons/Trash.svg"
												alt="Drawer Icon"
												className="w-5 h-5 md:block hidden"
											/>
										</button>
									)}
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default LocationsTable;
